/**
 * Dados que a casca mostra (nome do escritório, sino, favoritos), todos de
 * endpoints que já existem. Ficam em cache (React Query) para o menu, o topo e
 * a Home não buscarem a mesma coisa três vezes.
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import apiFetch from '../../apiClient';
import { useAuth } from '../../AuthContext';
import { useRecentes } from './recentes';

async function buscarJson(caminho) {
  const res = await apiFetch(caminho);
  const corpo = await res.json().catch(() => ({}));
  if (!res.ok) {
    const erro = new Error(`Falha ao buscar ${caminho}`);
    erro.status = res.status;
    throw erro;
  }
  return corpo;
}

/** Lista que vem como `{data: [...]}`, `{clientes: [...]}` ou a própria lista. */
export function listaDe(corpo, chave) {
  if (Array.isArray(corpo)) return corpo;
  if (Array.isArray(corpo?.data)) return corpo.data;
  if (chave && Array.isArray(corpo?.[chave])) return corpo[chave];
  return [];
}

/** Nome, logo e cor principal do escritório: os dados públicos da marca (os mesmos da proposta). */
export function useEscritorio() {
  const { usuarioLogado } = useAuth();
  const tenant = usuarioLogado?.tenant_id;
  const q = useQuery({
    queryKey: ['casca', 'escritorio', tenant],
    queryFn: () => buscarJson(`/api/brand/publico?tenant=${encodeURIComponent(tenant)}`),
    enabled: Boolean(tenant),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  return { nome: q.data?.nome_escritorio || '', logo: q.data?.logo_url || '', cor: q.data?.cor_primaria || '' };
}

export async function lerDados(caminho, opcoes) {
  const res = await apiFetch(caminho, opcoes);
  const corpo = await res.json().catch(() => ({}));
  if (!res.ok || corpo?.success === false) {
    const erro = new Error(`Falha ao buscar ${caminho}`);
    erro.status = res.status;
    erro.detail = corpo?.detail;
    throw erro;
  }
  return corpo?.data ?? corpo;
}

export const chavesDosAlertas = { alertas: ['alertas'], notificacoes: ['notificacoes', 'minhas'] };
const SEM_PRAZOS = { atrasados: [], hoje: [], semana: [] };

/**
 * Os alertas do escritório (Alertas.dc.html) e o cartão de prazos. A tela de
 * Alertas, a Home (prazos atrasados) e a busca do topo (prazos) leem daqui.
 */
export function useAlertas() {
  const { usuarioLogado } = useAuth();
  const q = useQuery({
    queryKey: chavesDosAlertas.alertas,
    queryFn: () => lerDados('/api/alertas'),
    enabled: Boolean(usuarioLogado),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: false,
  });
  return {
    alertas: Array.isArray(q.data?.alertas) ? q.data.alertas : [],
    prazos: q.data?.prazos || SEM_PRAZOS,
    podeDispensar: Boolean(q.data?.recursos?.dispensar),
    carregando: q.isPending,
    erro: q.error || null,
    recarregar: q.refetch,
  };
}

/** As notificações da pessoa (o sino e a aba "Minhas notificações"). */
export function useMinhasNotificacoes() {
  const { usuarioLogado } = useAuth();
  const q = useQuery({
    queryKey: chavesDosAlertas.notificacoes,
    queryFn: () => lerDados('/api/notificacoes/minhas?limite=50'),
    enabled: Boolean(usuarioLogado),
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    retry: false,
  });
  return {
    itens: Array.isArray(q.data?.itens) ? q.data.itens : [],
    naoLidas: Number(q.data?.nao_lidas) || 0,
    equipe: q.data?.equipe !== false,
    carregando: q.isPending,
    erro: q.error || null,
    recarregar: q.refetch,
  };
}

/** Processos marcados com estrela (o filtro "importantes" da lista de processos). */
export function useProcessosFavoritos(ativo) {
  const { usuarioLogado } = useAuth();
  const q = useQuery({
    queryKey: ['casca', 'favoritos', usuarioLogado?.id],
    queryFn: () => buscarJson('/api/juridico/processos?importantes=true&per_page=5'),
    enabled: Boolean(usuarioLogado) && ativo,
    staleTime: 60 * 1000,
    retry: false,
  });
  return { itens: listaDe(q.data), carregando: q.isPending && ativo, erro: q.error };
}

/** Nome curto de um processo para menu e busca: título, ou número, ou referência. */
export function nomeDoProcesso(p) {
  return p?.titulo || p?.cliente_nome || p?.cnj || p?.referencia_interna || `Processo ${p?.id}`;
}

/** Detalhe de um processo para a linha de baixo: número do tribunal ou referência, e a ação. */
export function detalheDoProcesso(p) {
  return [p?.cnj || p?.referencia_interna, p?.acao].filter(Boolean).join(', ');
}

/**
 * As fichas abertas recentemente, com o nome buscado na API (que confere o
 * acesso). Ficha que a pessoa não pode mais ver, ou que foi apagada, some da lista.
 */
export function useRecentesComNome(limite = 6) {
  const { usuarioLogado } = useAuth();
  const recentes = useRecentes(usuarioLogado?.id).slice(0, limite);
  const consultas = useQueries({
    queries: recentes.map((r) => ({
      queryKey: ['casca', 'ficha', r.tipo, String(r.id)],
      queryFn: () => buscarJson(r.tipo === 'processo' ? `/api/juridico/processos/${r.id}` : `/api/crm/${r.id}`),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });
  const itens = recentes
    .map((r, i) => {
      const dados = consultas[i]?.data;
      const registro = dados?.data && !Array.isArray(dados.data) ? dados.data : dados;
      if (!registro) return null;
      return r.tipo === 'processo'
        ? { ...r, registro, icone: 'folder_open', titulo: nomeDoProcesso(registro), detalhe: `Processo, ${detalheDoProcesso(registro) || 'sem número'}` }
        : { ...r, registro, icone: registro.tipo_pessoa === 'PJ' ? 'apartment' : 'person', titulo: registro.nome || `Cliente ${r.id}`, detalhe: 'Cliente' };
    })
    .filter(Boolean);
  const carregando = consultas.some((c) => c.isPending);
  return { itens, carregando };
}
